import React, { Fragment, useEffect, useMemo, useState } from 'react'
import { getEventsLogVisitors, getParticipantsList, getVisitorEventsList } from '../../../axios/Visitor/VisitorApi';
import { useTranslation } from 'react-i18next';
import { generateTranslateMuiTable } from '../../../utils/Function/commonFunction';
import { StyledDataGrid } from '../../../components/StyledComponents';
import CustomPagination from '../CustomPagination';
import NoRowOverlay from '../../../assets/images/icons/NoRowOverlay';
import CustomColumnsPanel from '../../../components/CustomColumnsPanel';
import DeleteIcon16pxSvg from '../../../assets/images/icons/DeleteIcon16pxSvg';
import RemoveUserDialog from './RemoveUserDialog';
import { useAuth } from '../../../context/AuthContext';
import { DateTime } from "luxon";
import dayjs from "dayjs";
import { format } from 'date-fns';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Tooltip } from '@mui/material';
import EditEventLogDialog from './EditEventLogDialog';
import DetailEventLogDialog from './DetailEventLogDialog ';
import { useLoading } from '../../../context/LoadingContext';
import { endOfDay, startOfDay } from "date-fns";
import RemoveEventLogDialog from './RemoveEventLogDialog';
export const EVENT_STATUS = {
    checkInOut: 0,
    missingCheckIn: 1,
    missingCheckOut: 2,
};

const VisitorEventLogGridInfoTable = ({ selectedDateTime, dataInfo, data, setData, onRowClick, refreshData, onRefresh, onPaginationChange }) => {
    const { t } = useTranslation();
    // const [data, setData] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [filteredRowCount, setFilteredRowCount] = useState(0);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [removeUserOpen, setRemoveUserOpen] = useState(false);
    const { dateTimeFormat } = useAuth();
    const [detailOpen, setDetailOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [show, hide] = useLoading();

    const fetchData = async () => {
        const payload = {
            eventId: dataInfo?.id || null,
            isError: null,
            isEnabled: null,
            pageNo: page + 1,
            recordPerPage: pageSize,
            from: startOfDay(selectedDateTime?.startDate),
            to: endOfDay(selectedDateTime?.endDate),
        };
        try {
            show();
            const response = await getEventsLogVisitors(payload);

            setData(response?.data?.data || []);
            setRowCount(response.data.pagination.totalElements);
            setFilteredRowCount(response.data.pagination.totalElements);

            const total = response?.data?.pagination?.totalPages
                || Math.ceil((response?.data?.pagination?.totalElements || 0) / pageSize)
                || 1;

            onPaginationChange?.({
                totalPages: total,
                pageSize,
            });

        } catch (error) {
            console.error(error);
        } finally {
            hide();
        }
    };

    useEffect(() => {
        fetchData();
    }, [dataInfo?.id, refreshData, selectedDateTime]);

    const handleCloseRemoveUser = () => {
        setRemoveUserOpen(false);
    };

    const handleOpenRemoveUser = () => {
        setRemoveUserOpen(true);
    };

    const tableTranslated = useMemo(() => generateTranslateMuiTable(t), [t]);

    const columnData = [
        { field: 'fullName', headerName: t('common.full-name'), hideable: true, flex: 1, },
        {
            field: "eventType",
            headerName: t("common.content-115"),
            hideable: true,
            flex: 1,

            valueGetter: (params) => {
                const eventType = params;

                if (!eventType) return "-";

                return t(`attendance.${eventType}`);
            },

            renderCell: (params) => {
                const label = t(`attendance.${params.row.eventType}`);

                const missingEvent =
                    params.row.missingEvent !== EVENT_STATUS.checkInOut;

                const missingLabel = `(${t(
                    `attendance.${params.row.missingEvent}`
                )})`;

                const eventDate = new Date(params.row.timeGMT);

                const today = new Date();
                today.setHours(0, 0, 0, 0);

                const isPastEvent = eventDate < today;

                switch (params.row.eventType) {
                    case "CHECK_OUT":
                        return (
                            <div
                                className="text-blue-500 flex items-center justify-start gap-1.5 uppercase"
                                style={{ height: "100%" }}
                            >
                                <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                                <p className="text-xs font-bold leading-5">{label}</p>
                                {missingEvent && isPastEvent && (
                                    <p className="text-xs font-bold text-red-500 leading-5">
                                        {missingLabel}
                                    </p>
                                )}
                            </div>
                        );

                    case "CHECK_IN":
                        return (
                            <div
                                className="text-green-500 flex items-center justify-start gap-1.5 uppercase"
                                style={{ height: "100%" }}
                            >
                                <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                                <p className="text-xs font-bold leading-5">{label}</p>
                                {missingEvent && isPastEvent && (
                                    <p className="text-xs font-bold text-red-500 leading-5">
                                        {missingLabel}
                                    </p>
                                )}
                            </div>
                        );

                    default:
                        return (
                            <div
                                className="text-yellow-500 flex items-center justify-start gap-1.5 uppercase"
                                style={{ height: "100%" }}
                            >
                                <span className="w-1.5 h-1.5 rounded-full bg-yellow-500"></span>
                                <p className="text-xs font-bold leading-5">{label}</p>
                            </div>
                        );
                }
            },
        },
        {
            field: "timeGMT",
            headerName: `${t("common.content-036")} (GMT)`,
            hideable: true,
            flex: 1,
            rowSpanValueGetter: (value, row) =>
                row ? `${row.id}-${row.timeGMT}` : value,

            valueFormatter: (value) => {
                if (!value) return "-";

                // Parse UTC (hỗ trợ cả ISO và yyyy-MM-dd HH:mm:ss)
                let dt = DateTime.fromISO(value, { zone: "utc" });

                if (!dt.isValid) {
                    dt = DateTime.fromFormat(
                        value,
                        "yyyy-MM-dd HH:mm:ss",
                        { zone: "utc" }
                    );
                }

                if (!dt.isValid) return "-";

                // ✅ Convert sang timezone của company (KHÔNG dùng timezone máy)
                const browserTZ = dayjs.tz.guess();
                const companyDt = browserTZ
                    ? dt.setZone(browserTZ)
                    : dt.toLocal(); // fallback an toàn

                return companyDt.toFormat(
                    dateTimeFormat || "dd/MM/yyyy HH:mm:ss"
                );
            }
        },
        {
            field: "createdTime",
            headerName: t("common.created-time"),
            hideable: true,
            flex: 1,
            rowSpanValueGetter: (value, row) =>
                row ? `${row.id}-${row.createdTime}` : value,
            valueFormatter: (value) => {
                if (value === null || value === undefined) return "-";

                // Sử dụng date-fns để chuyển đổi định dạng
                const formattedDate = value
                    ? format(new Date(value), dateTimeFormat || "dd/MM/yyyy HH:mm:ss")
                    : "";

                return formattedDate;
            },
            // valueGetter: (params) => (params.row.createdTime ? params.row.createdTime : '-'),
        },
        {
            field: "options",
            headerName: t("common.options"),
            sortable: false,
            filterable: false,
            minWidth: 200,
            renderCell: (params) => (
                <div
                    className="flex gap-2 items-center justify-start h-full"
                    onClick={(e) => e.stopPropagation()}
                >
                    <Tooltip
                        title={t("button.detail")}
                        placement="right"
                        arrow
                    >
                        <button
                            className="p-2 rounded-lg hover:bg-blue-100 transition cursor-pointer flex items-center justify-center"
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedRow(params.row);
                                setDetailOpen(true);
                            }}
                        >
                            <InfoOutlinedIcon
                                className="w-5 h-5 text-[#0057FF]"
                            />
                        </button>
                    </Tooltip>

                    <Tooltip
                        title={t("button.remove")}
                        placement="right"
                        arrow
                    >
                        <button
                            className="p-2 rounded-lg hover:bg-red-100 transition cursor-pointer"
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedRow(params.row);
                                handleOpenRemoveUser();
                            }}
                        >
                            <DeleteIcon16pxSvg
                                className="w-5 h-5"
                                color="#D00D00"
                            />
                        </button>
                    </Tooltip>
                </div >
            )
        },
    ];

    const handleRowsPerPageChange = (newPageSize) => {
        // newPageSize ở đây tuỳ CustomPagination, có thể là event hoặc number
        const size =
            typeof newPageSize === "number"
                ? newPageSize
                : parseInt(newPageSize.target.value, 10);

        setPage((prevPage) => {
            // index của record đầu tiên đang xem
            const firstRowIndex = prevPage * pageSize;

            // page mới sao cho vẫn chứa record đó
            const newPage = Math.floor(firstRowIndex / size);

            // phòng trường hợp vượt quá số trang
            const maxPage = Math.max(
                0,
                Math.ceil(filteredRowCount / size) - 1
            );

            return Math.min(newPage, maxPage);
        });

        setPageSize(size);
    };

    const handleRowClick = (params) => {
        const rowData = params.row;
        if (rowData) {
            onRowClick(rowData);
        }
    };

    const hasRows = data.length > 0;
    const gridHeight = hasRows ? 500 : 360;

    return (
        <>
            <Fragment>
                <StyledDataGrid
                    showColumnVerticalBorder
                    sx={{
                        height: gridHeight,

                        "@media (max-width: 1919px)": {
                            height: 750,
                        },

                        ".MuiDataGrid-main": {
                            height: "70vh",
                        },

                        "& .MuiDataGrid-scrollbar--vertical, & .MuiDataGrid-scrollbar--horizontal":
                        {
                            display: "none !important",
                        },

                        "& .MuiDataGrid-scrollbarFiller": {
                            display: "none !important",
                        },
                        "& .MuiDataGrid-columnHeaders": {
                            paddingRight: 0,
                        },
                        "& .MuiDataGrid-topContainer": {
                            paddingRight: 0,
                        },

                        "&": {
                            "--DataGrid-scrollbarSize": "0px",
                        },

                        "& .MuiDataGrid-virtualScroller": {
                            scrollbarWidth: "none",
                        },
                        "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                            width: 0,
                            height: 0,
                        },
                        "&:hover .MuiDataGrid-virtualScroller, &:focus-within .MuiDataGrid-virtualScroller":
                        {
                            scrollbarWidth: "thin",
                        },
                        "&:hover .MuiDataGrid-virtualScroller::-webkit-scrollbar, &:focus-within .MuiDataGrid-virtualScroller::-webkit-scrollbar":
                        {
                            width: 10,
                            height: 10,
                        },

                        '& [class*="MuiDataGrid-scrollbar"][class*="--horizontal"]': {
                            display: "none !important",
                        },

                        "& input[type=number]::-webkit-inner-spin-button, \
 & input[type=number]::-webkit-outer-spin-button": {
                            WebkitAppearance: "none",
                            margin: 0,
                        },

                        "& input[type=number]": {
                            textAlign: "center",
                            MozAppearance: "textfield",
                            WebkitAppearance: "none",
                            padding: "5px !important",
                            height: "100%",
                            pointerEvents: "none",
                            caretColor: "transparent",
                            userSelect: "none",
                            paddingTop: "8px !important",
                            paddingBottom: "8px !important"
                        },
                    }}
                    rows={data || []}
                    rowCount={rowCount}
                    paginationMode="client"
                    columns={columnData}
                    // loading={gridLoading}
                    getRowId={(r) => r.eventAttendanceId}
                    slots={{
                        pagination: CustomPagination,
                        noRowsOverlay: NoRowOverlay,
                        columnsPanel: CustomColumnsPanel,
                    }}
                    slotProps={{
                        pagination: {
                            page,
                            count: rowCount,
                            rowsPerPage: pageSize,
                            onPageChange: setPage,
                            onRowsPerPageChange: handleRowsPerPageChange,
                        },
                    }}
                    localeText={tableTranslated}
                    onRowClick={handleRowClick} />
            </Fragment>

            {detailOpen && <DetailEventLogDialog id={selectedRow?.eventAttendanceId} onClose={() => setDetailOpen(false)} onRefresh={onRefresh} refreshData={refreshData} />}
            {removeUserOpen && <RemoveEventLogDialog dataInfo={dataInfo} id={selectedRow?.eventAttendanceId} onClose={() => setRemoveUserOpen(false)} onRefresh={onRefresh} refreshData={refreshData} />}
        </>
    )
}

export default VisitorEventLogGridInfoTable